import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  AdminAreaIcon,
  AdminTeacherRoleIcon,
  ArrowChevronDownIcon,
  ArrowChevronLeftIcon,
  ArrowChevronRightIcon,
  ArrowChevronUpIcon,
  ArrowRightIcon,
  AudioNextIcon,
  AudioPreviousIcon,
  BackArrowIcon,
  CalendarBlank,
  CalendarGoToTodayIcon,
  CameraIcon,
  CaretDownIcon,
  CheckCircleIcon,
  CheckCircleInverted,
  CheckedBoxIcon,
  CheckMarkIcon,
  ClassroomIcon,
  ClearIcon,
  CogIcon,
  CogIcon2,
  CopyIcon,
  CustomCardIcon,
  CxAdminAreaIcon,
  DeleteAllIcon,
  DistrictAdminAreaIcon,
  DownloadIcon,
  EllipseIcon,
  EnvelopeIcon,
  ExclamationInCircle,
  ExportIcon,
  EyeIcon,
  FileIcon,
  FlipIcon,
  GridIcon,
  HamburgerIcon,
  ImportFileIcon,
  InfoCircleIcon,
  InfoCircleInvertedIcon,
  KeyGlowIcon,
  KeyHorizontal,
  LogoutIcon,
  MaskIcon,
  MoveHandleIcon,
  NavigateToFolder,
  FileTransfer,
  NeutralIcon,
  NotepadIcon,
  OpenBookIcon,
  ParentAreaIcon,
  ParentRoleIcon,
  PasteIcon,
  PencilIcon,
  PrinterIcon,
  QrCodeNewIcon,
  QrCodeScanIcon,
  QuestionMarkIcon,
  ResizeHandleIcon,
  SchoolAdminAreaIcon,
  SmileyFaceIcon,
  SpeakerIcon,
  StudentRoleIcon,
  SupportAreaIcon,
  TCrossIcon,
  TeacherAreaIcon,
  ThumbtackIcon,
  TrashCanIcon,
  UncheckedBoxIcon,
  UploadIcon,
  VerticalEllipsesIcon,
  WrenchesIcon,
  XCrossCircleIcon,
  XCrossIcon,
  ZbLogoIcon,
  MagnifyingGlassWithMinusIcon,
  MagnifyingGlassWithPlusIcon,
  EraserIcon,
  PencilDrawIcon,
  AudioFastForwardIcon,
  AudioPauseIcon,
  AudioPlayIcon,
  AudioRewindIcon,
  WarningCircleIcon,
} from '@assets/zbp-icons/all-svgs';
import { notepadWithTIcon } from '@assets/zbp-icons/notepad-with-t';
import { zbpIconNames } from './zbp-icon-names';

/**
 * # Icon Manual Parameterization is Required!
 *
 * SVGs that are added for this component need some manual processing to fully support all features.
 * The colors for the SVG should be parameterized such that they can be easily overridden, and have consistent defaults.
 *
 * - The primary color, usually black, should be replaced with `var(--zbp-svg-primary-color, black)`
 * in each place that it is referenced in the SVG markup.
 *
 * - The secondary color, usually white, should be replaced with `var(--zbp-svg-secondary-color, white)`
 * in each place that it is referenced in the SVG markup.
 *
 * A good reference for advanced usage of the pattern would be
 * `/assets/highlights/svgs/area-icons/school-admin-area-icon.ts`, which uses the primary and
 * secondary colors in both fill and stroke to achieve the desired image.
 *
 * ## SVG Parameterization Example:
 * Here the circle will default to have a stroke of black and fill of white, but can be replaced via CSS.
 * ```
 * <svg class="styledCircle" height="100" width="100" xmlns="http://www.w3.org/2000/svg">
 *   <circle r="45" cx="50" cy="50"
 *     stroke="var(--zbp-svg-primary-color, black)"
 *     fill="var(--zbp-svg-secondary-color, white)"
 * />
 * </svg>
 * ```
 *
 * ## Using the Parameters in CSS Example:
 * If we wanted to replace those colors on that SVG specifically, we could then use the following CSS.
 * ```
 * .styledCircle {
 *    --zbp-svg-primary-color: lime;
 *    --zbp-svg-secondary-color: magenta;
 * }
 * ```
 */
@Component({
  selector: 'zbp-icon',
  templateUrl: './zbp-icon.html',
  styleUrls: ['./zbp-icon.scss'],
  imports: [CommonModule]
})
export class ZbpIconComponent {
  private iconNameToComponentMap = {
    [zbpIconNames.arrow_right]: ArrowRightIcon,
    [zbpIconNames.arrow_chevron_right]: ArrowChevronRightIcon,
    [zbpIconNames.arrow_chevron_left]: ArrowChevronLeftIcon,
    [zbpIconNames.arrow_chevron_up]: ArrowChevronUpIcon,
    [zbpIconNames.arrow_chevron_down]: ArrowChevronDownIcon,
    [zbpIconNames.smileyFace]: SmileyFaceIcon,
    [zbpIconNames.thumbtack]: ThumbtackIcon,
    [zbpIconNames.printer]: PrinterIcon,
    [zbpIconNames.x_cross]: XCrossIcon,
    [zbpIconNames.flip]: FlipIcon,
    [zbpIconNames.t_cross]: TCrossIcon,
    [zbpIconNames.pencil]: PencilIcon,
    [zbpIconNames.ellipse]: EllipseIcon,
    [zbpIconNames.eye]: EyeIcon,
    [zbpIconNames.custom_card]: CustomCardIcon,
    [zbpIconNames.download]: DownloadIcon,
    [zbpIconNames.neutral]: NeutralIcon,
    [zbpIconNames.notepad]: NotepadIcon,
    [zbpIconNames.notepadWithT]: notepadWithTIcon,
    [zbpIconNames.backArrow]: BackArrowIcon,
    [zbpIconNames.camera]: CameraIcon,
    [zbpIconNames.calendar_blank]: CalendarBlank,
    [zbpIconNames.calendar_go_to_today]: CalendarGoToTodayIcon,
    [zbpIconNames.copy]: CopyIcon,
    [zbpIconNames.paste]: PasteIcon,
    [zbpIconNames.vertical_ellipses]: VerticalEllipsesIcon,
    [zbpIconNames.warningCircleIcon]: WarningCircleIcon,
    [zbpIconNames.check_mark]: CheckMarkIcon,
    [zbpIconNames.check_circle]: CheckCircleIcon,
    [zbpIconNames.trash_can]: TrashCanIcon,
    [zbpIconNames.key_glow]: KeyGlowIcon,
    [zbpIconNames.cog]: CogIcon,
    [zbpIconNames.cog2]: CogIcon2,
    [zbpIconNames.mask]: MaskIcon,
    [zbpIconNames.envelope]: EnvelopeIcon,
    [zbpIconNames.delete_all]: DeleteAllIcon,
    [zbpIconNames.x_cross_circle]: XCrossCircleIcon,
    [zbpIconNames.export]: ExportIcon,
    [zbpIconNames.upload]: UploadIcon,
    [zbpIconNames.file]: FileIcon,
    [zbpIconNames.info_circle]: InfoCircleIcon,
    [zbpIconNames.info_circle_inverted]: InfoCircleInvertedIcon,
    [zbpIconNames.unchecked_box]: UncheckedBoxIcon, // TODO consider removing if still unused
    [zbpIconNames.checked_box]: CheckedBoxIcon, // TODO consider removing if still unused
    [zbpIconNames.move_handle]: MoveHandleIcon,
    [zbpIconNames.resize_handle]: ResizeHandleIcon,
    [zbpIconNames.wrenches]: WrenchesIcon,
    [zbpIconNames.parent_role]: ParentRoleIcon,
    [zbpIconNames.speaker_icon]: SpeakerIcon,
    [zbpIconNames.student_role]: StudentRoleIcon,
    [zbpIconNames.admin_teacher_role]: AdminTeacherRoleIcon,
    [zbpIconNames.admin_area]: AdminAreaIcon,
    [zbpIconNames.cx_admin_area]: CxAdminAreaIcon,
    [zbpIconNames.district_admin_area]: DistrictAdminAreaIcon,
    [zbpIconNames.school_admin_area]: SchoolAdminAreaIcon,
    [zbpIconNames.support_area]: SupportAreaIcon,
    [zbpIconNames.teacher_area]: TeacherAreaIcon,
    [zbpIconNames.parent_area]: ParentAreaIcon,
    [zbpIconNames.exclamation_in_circle]: ExclamationInCircle,
    [zbpIconNames.check_circle_inverted]: CheckCircleInverted,
    [zbpIconNames.key_horizontal]: KeyHorizontal,
    [zbpIconNames.import_file]: ImportFileIcon,
    [zbpIconNames.qr_code_scan]: QrCodeScanIcon,
    [zbpIconNames.qr_code_new]: QrCodeNewIcon,
    [zbpIconNames.hamburger]: HamburgerIcon,
    [zbpIconNames.caret_down]: CaretDownIcon,
    [zbpIconNames.question_mark]: QuestionMarkIcon,
    [zbpIconNames.zb_logo]: ZbLogoIcon,
    [zbpIconNames.classroom]: ClassroomIcon,
    [zbpIconNames.open_book]: OpenBookIcon,
    [zbpIconNames.logout]: LogoutIcon,
    [zbpIconNames.clear]: ClearIcon,
    [zbpIconNames.navigateToFolder]: NavigateToFolder,
    [zbpIconNames.fileTransfer]: FileTransfer,
    [zbpIconNames.magnifyingGlassWithMinusIcon]: MagnifyingGlassWithMinusIcon,
    [zbpIconNames.magnifyingGlassWithPlusIcon]: MagnifyingGlassWithPlusIcon,
    [zbpIconNames.eraserIcon]: EraserIcon,
    [zbpIconNames.pencilDrawIcon]: PencilDrawIcon,
    [zbpIconNames.audioNextIcon]: AudioNextIcon,
    [zbpIconNames.audioPreviousIcon]: AudioPreviousIcon,
    [zbpIconNames.grid]: GridIcon,
    [zbpIconNames.audioPlayIcon]: AudioPlayIcon,
    [zbpIconNames.audioPauseIcon]: AudioPauseIcon,
    [zbpIconNames.audioRewindIcon]: AudioRewindIcon,
    [zbpIconNames.audioFastForwardIcon]: AudioFastForwardIcon,
  };

  @Input() set iconName(value: string) {
    this._iconName = value;
    this.loadSvg();
  }

  protected _iconName: string = null;

  /**
   * Overrides the default --zbp-svg-primary-color variable for the icon.
   * can accept a CSS variable e.g.: var(--primaryFade);
   */
  @Input() primaryColor?: string;

  /**
   * Overrides the default --zbp-svg-secondary-color variable for the icon.
   * can accept a CSS variable e.g.: var(--primaryFade);
   */
  @Input() secondaryColor?: string;

  /** Adjusts the size of the icon rendered.
   * <ul>
   * <li><strong>default</strong>: lets the icon do whatever it wants.</li>
   * <li><strong>font-size</strong>: forces icon to match font size.</li>
   * </ul>
   */
  @Input() iconSize: 'font-size' | 'default' = 'default';

  svgIcon: SafeHtml;

  constructor(private sanitizer: DomSanitizer) {
  }

  /**
   * Retrieves and sanitizes the SVG icon based on the provided iconName input.
   * If the icon does not exist, logs an error and sets the svgIcon to an empty string.
   * @private
   */
  private loadSvg(): void {
    if (this._iconName && this.iconNameToComponentMap[this._iconName]) {
      this.svgIcon = this.sanitizer.bypassSecurityTrustHtml(this.iconNameToComponentMap[zbpIconNames[this._iconName]]);
    } else {
      console.error(`Icon ${this._iconName} not found.`);
      this.svgIcon = this.sanitizer.bypassSecurityTrustHtml('');
    }
  }
}
